// User-defined attributes: one LINE's additional details — the Details chip a
// grid row carries, and the drawer it opens (prototype screen 2).
//
// The drawer is the shared FormRenderer over ONLY the attributes that apply to
// the row (`withUdaColumns` over an empty copy of the line form), grouped by
// their set, plus the "why" line (the row's deciding values) and the matching
// picture (`pictures.by_value` of the first applicable attribute that has one
// for the row's value). It edits the row through the grid's own cell-change
// path, so item change and validation behave exactly as in a cell.

import { Image as ImageIcon } from 'lucide-react'
import type { SqlModel } from '../txn/types'
import FormRenderer from '../txn/FormRenderer'
import ObjectDrawer from '../txn/screen/ObjectDrawer'
import { columnFormKey, ddlbDisplayLabel } from '../txn/utils'
import { tw } from '../testHooks'
import { FlagBadges } from './flags'
import { detailsChipText, driversSentence, isUdaColumn, withUdaColumns, type UdaDetailsCounts } from './merge'
import type { UdaColumnDef, UdaFlag, UdaRowResolution } from './types'

/** The drawer's width (the prototype's 380px side panel, plus padding). */
export const UDA_DRAWER_WIDTH = 420

/** The row's Details chip: `filled/total · N required missing`; the dot is
 *  red while a required attribute is empty. */
export function UdaDetailsChip({ counts, onOpen, label, disabled }: {
  counts: UdaDetailsCounts
  onOpen: () => void
  /** Accessible name ("Open details for line 3"). */
  label: string
  disabled?: boolean
}) {
  if (counts.total === 0) return null
  const ok = counts.missing === 0
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen() }} disabled={disabled}
      aria-label={label} title={label} {...tw('action', 'uda_details')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
        border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)',
        color: 'var(--color-text)', borderRadius: 999, padding: '3px 10px',
        fontSize: 13, fontWeight: 500, fontFamily: 'inherit', lineHeight: 1.35,
        cursor: disabled ? 'default' : 'pointer',
      }}>
      <span aria-hidden style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: ok ? 'var(--color-success)' : 'var(--color-error)',
      }} />
      {detailsChipText(counts)}
    </button>
  )
}

/** The picture an applicable attribute holds for the row's current value
 *  (`pictures.by_value`): an uploaded image or a colour swatch. When the
 *  attribute has pictures but none for this value, a placeholder says so
 *  (prototype: "No picture for this colour yet"). `subject` names the line
 *  ("Cotton crew T-shirt") for the caption. */
export function matchingPicture(
  columns: readonly UdaColumnDef[], values: Record<string, unknown>, attrKeys: readonly string[] | null,
  subject?: string,
): { src?: string; swatch?: string; caption: string; missing?: boolean } | null {
  const keep = attrKeys ? new Set(attrKeys) : null
  let missing: { caption: string; missing: true } | null = null
  for (const c of columns) {
    if (!isUdaColumn(c) || c.uda.driver || !c.pictures?.by_value) continue
    if (keep && !keep.has(String(c.uda.key))) continue
    const raw = values[columnFormKey(c)]
    const code = raw === null || raw === undefined ? '' : String(raw).trim()
    if (!code) continue
    const pic = c.pictures.by_value[code]
    const heading = String(c.heading || c.name)
    if (!pic || (!pic.asset && !pic.swatch)) {
      missing = missing || { caption: `No picture for this ${heading.toLowerCase()} yet`, missing: true }
      continue
    }
    const shown = ddlbDisplayLabel(code, c) || code
    return {
      src: pic.asset || undefined,
      swatch: pic.swatch || undefined,
      caption: pic.caption || (subject ? `Picture for ${subject} in ${shown}` : `${heading}: ${shown}`),
    }
  }
  return missing
}

export interface UdaRowDrawerProps {
  /** "Line 3 · Cotton crew T-shirt". */
  title: string
  /** What the line is ("Cotton crew T-shirt") — the picture's caption. */
  subject?: string
  /** The line form (its own columns are NOT shown — only attributes). */
  sm: SqlModel
  /** The attribute columns the grid knows (the union over its rows). */
  columns: readonly UdaColumnDef[]
  /** The row's resolution (null while it is not known yet). */
  resolution: UdaRowResolution | null
  values: Record<string, unknown>
  errors?: Record<string, string>
  /** The flags raised on the row (already evaluated). */
  flags?: readonly UdaFlag[]
  headerValues?: Record<string, unknown>
  onChange: (formKey: string, value: unknown) => void
  readOnly?: boolean
  project: string
  objName: string
  formNo: string
  /** A failed resolve: shown quietly; the row stays usable. */
  notice?: string | null
  /** Lock state of the row's attributes ({form_key: 1 lock | 0 unlock}). */
  dynamicProtect?: Record<string, 0 | 1>
  onClose: () => void
}

export default function UdaRowDrawer({
  title, subject, sm, columns, resolution, values, errors, flags, headerValues, onChange,
  readOnly, project, objName, formNo, notice, dynamicProtect, onClose,
}: UdaRowDrawerProps) {
  const attrKeys = resolution ? resolution.attr_keys : null
  // Only the attributes: an empty copy of the line form, merged.
  const base: SqlModel = { ...sm, columns: [], actions: [] }
  const model = withUdaColumns(base, columns, attrKeys || [])
  const shown = (model.columns || []).some(w => w.column && !w.column.uda?.driver)
  const why = resolution ? driversSentence(columns, resolution.drivers) : ''
  const picture = matchingPicture(columns, values, attrKeys, subject)
  return (
    <ObjectDrawer title={title} objType="uda" onClose={onClose} width={UDA_DRAWER_WIDTH} twDialog="uda_details">
      <div style={{ padding: 14, display: 'grid', gap: 14, fontSize: 14 }}>
        {why && (
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Asked because <b style={{ color: 'var(--color-text)', fontWeight: 600 }}>{why}</b>
          </div>
        )}
        {notice && (
          <div role="status" style={{
            fontSize: 13, color: 'var(--color-text)', background: 'var(--color-warning-weak)',
            borderRadius: 8, padding: '8px 10px',
          }}>{notice}</div>
        )}
        {picture && (
          <div>
            <div style={{
              display: 'grid', placeItems: 'center', aspectRatio: '4 / 3', maxWidth: '100%',
              background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
              borderRadius: 8, overflow: 'hidden',
            }}>
              {picture.missing
                ? <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{picture.caption}</span>
                : picture.src
                ? <img src={picture.src} alt={picture.caption} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                : picture.swatch
                  ? <span aria-hidden style={{ width: '46%', aspectRatio: '1 / 1', borderRadius: 12, background: picture.swatch, border: '1px solid var(--color-border-strong)' }} />
                  : <ImageIcon size={40} />}
            </div>
            {!picture.missing && (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 6 }}>{picture.caption}</div>
            )}
          </div>
        )}
        {shown ? (
          <div style={{ margin: '0 -14px' }}>
            <FormRenderer
              model={model}
              values={values}
              errors={errors || {}}
              onChange={onChange}
              readOnlyKey={false}
              viewOnly={readOnly}
              project={project}
              objName={objName}
              formNo={formNo}
              headerValues={headerValues}
              dynamicProtect={dynamicProtect}
              layout="compact"
            />
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {resolution ? 'No additional details apply to this line.' : 'Working out which details apply…'}
          </div>
        )}
        <FlagBadges flags={flags} />
      </div>
    </ObjectDrawer>
  )
}
